<script lang="ts">
  import type { TokenStatus } from '@do/models';

  import { Chip } from '@shared/ui/chip/ui';
  import { Content } from '@shared/ui/content';
  import type { Block } from '@shared/ui/content/types';
  import { FormattedList } from '@shared/ui/formatted-list';
  import type { OriginalSizeSingle } from '@shared/ui/list/types';
  import { ReadMore } from '@shared/ui/read-more';
  import { Switcher } from '@shared/ui/switcher';
  import PoDetails from '@shared/ui/tabs/ui/po-details.svelte';
  import { is } from '@shared/utils';

  import type { ArtAsset } from '@entities/art-asset';
  import { type TokenHistory } from '@entities/history';

  import type { HashDataTemp } from '@features/sale/sale-auction';

  import { buildFramedSizeParam, buildOriginalSizeParam } from '../libs';
  import type { ClassificationParam, TechParam, Tag } from '../types';
  import Artist from './artist.svelte';
  import DODetails from './do-details.svelte';
  import Tab from './tab.svelte';

  let activeTab = 0;
  const handleChange = (activeIndex: number) => {
    activeTab = activeIndex;
  };

  export let artists: ArtAsset['artists'] = [];
  export let locations: ArtAsset['locations'] | null = null;
  export let descriptionBlocks: Block[];
  export let originalSizeX: OriginalSizeSingle;
  export let originalSizeY: OriginalSizeSingle;
  export let originalUnits: string | null;
  export let framedSizeX: OriginalSizeSingle;
  export let framedSizeY: OriginalSizeSingle;
  export let framedSizeZ: OriginalSizeSingle;
  export let creationYear: string | number | null = null;
  export let styles: ClassificationParam[] | null = null;
  export let medium: ClassificationParam[] | null = null;
  export let contractAddress: HashDataTemp;
  export let contractStandard: string;
  export let contractBlockchain: string;
  export let metadataStorage: string;
  export let digitalOriginalData: ArtAsset['digital_original'] | null = null;
  export let tags: Tag[];
  export let tokenStatus: TokenStatus;
  export let tokenHistory: TokenHistory['data'] | null | undefined;

  $: isMinted = tokenStatus !== 'draft';

  let detailsList: TechParam[];

  $: canvasSize =
    originalSizeX || originalSizeY ? buildOriginalSizeParam([originalSizeX, originalSizeY], originalUnits) : null;
  $: framing = buildFramedSizeParam([framedSizeX, framedSizeY, framedSizeZ]);

  $: detailsList = [
    {
      name: 'Contract Address',
      value: is(isMinted, contractAddress?.value),
      url: contractAddress?.explorerUrl,
    },
    {
      name: 'Token Standard',
      value: is(isMinted, contractStandard),
    },
    {
      name: 'Blockchain',
      value: is(isMinted, contractBlockchain),
    },
    {
      name: 'Metadata',
      value: is(isMinted, metadataStorage?.toUpperCase()),
    },
    {
      name: 'Resolution',
      value: digitalOriginalData?.mpx ? `${digitalOriginalData.mpx} MPX` : null,
    },
    {
      name: 'Dimensions',
      value:
        digitalOriginalData?.exif?.ImageWidth && digitalOriginalData?.exif?.ImageHeight
          ? `${digitalOriginalData.exif.ImageWidth}x${digitalOriginalData.exif.ImageHeight}`
          : null,
    },

    {
      name: 'Medium',
      value: digitalOriginalData?.exif?.FileType,
    },
    {
      name: 'Device',
      value: digitalOriginalData?.exif?.Make,
    },
    {
      name: 'Device model',
      value: digitalOriginalData?.exif?.Model,
    },
    {
      name: 'Lense',
      value: digitalOriginalData?.exif?.LensMake,
    },
    {
      name: 'Lense model',
      value: digitalOriginalData?.exif?.LensModel,
    },
    {
      name: 'Color space',
      value: digitalOriginalData?.exif?.ColorSpace,
    },
    {
      name: 'Color profile description',
      value: '48 bit color depth, 281 Trillion Colors',
    },
    {
      name: 'Metering mode',
      value: digitalOriginalData?.exif?.MeteringMode,
    },
    {
      name: 'F number',
      value: digitalOriginalData?.exif?.FNumber,
    },
    {
      name: 'Exposure program',
      value: digitalOriginalData?.exif?.ExposureProgram,
    },
    {
      name: 'Exposure time',
      value: digitalOriginalData?.exif?.ExposureTime,
    },
    {
      name: 'Focal length',
      value: digitalOriginalData?.exif?.FocalLength,
    },
    {
      name: 'Photographer',
      value: digitalOriginalData?.photographer
        ? `${digitalOriginalData.photographer.first_name} ${digitalOriginalData.photographer.last_name}`
        : 'Digital Original Studio',
    },
  ];

  let tabs: { name: string | null; href?: string }[] = [];

  $: tabs = [
    { name: 'About artwork' },
    { name: artists?.length ? 'Artist Bio' : null },
    { name: digitalOriginalData && tokenHistory?.length ? 'History' : null },
    { name: digitalOriginalData && digitalOriginalData.exif && digitalOriginalData.dng ? 'Digital Details' : null },
  ];
  $: tabs, (activeTab = 0); // set first tab active when tabs list changed

  $: preparedLocation = mapLocations(locations).join(', ');

  function mapLocations(locations: ArtAsset['locations']) {
    if (!locations || !locations.length) {
      return [];
    }

    return locations.reduce<string[]>((acc, location) => {
      let locationAddress = [];

      if (location.country) {
        locationAddress.push(location.country.value);
      }

      if (location.city) {
        locationAddress.push(location.city.value);
      }

      if (location.address) {
        locationAddress.push(location.address);
      }

      return [...acc, locationAddress.join(' - ')];
    }, []);
  }
</script>

<div>
  <div class="mb-10">
    <Switcher names="{tabs}" activeIndex="{activeTab}" on:change="{(e) => handleChange(e.detail)}" />
  </div>

  <Tab id="{0}" activeId="{activeTab}">
    {#if descriptionBlocks}
      <div class="mb-8 md:mb-10">
        <ReadMore collapseHeight="{240}">
          <Content blocks="{descriptionBlocks}" containerClasses="md:text-base" />
        </ReadMore>
      </div>
    {/if}

    <div class="mb-10 flex flex-col gap-5">
      <DODetails {digitalOriginalData} />
      <PoDetails {canvasSize} {framing} {styles} {medium} {creationYear} locations="{preparedLocation}" />
    </div>

    {#if tags?.length}
      <div class="mb-10">
        <p class="text-default-900 dark:text-dark-900 mb-4 font-medium uppercase">Tags</p>
        <div class="flex gap-2">
          {#each tags as tag}
            <Chip label="#{tag.name}" href="searchtag/{tag.name}/" />
          {/each}
        </div>
      </div>
    {/if}
    <!-- in schema we have the array but here will be only one gallery (or we show only first) -->
  </Tab>

  <Tab id="{1}" activeId="{activeTab}">
    {#if artists?.length}
      <div class="mb-10">
        <Artist {artists} />
      </div>
    {/if}
  </Tab>

  {#if tokenHistory}
    <Tab id="{2}" activeId="{activeTab}">
      <div class="mb-6">
        <slot name="history" />
      </div>
    </Tab>
  {/if}

  <Tab id="{3}" activeId="{activeTab}">
    <div class="mb-6">
      <FormattedList variant="primary" size="md" items="{detailsList}" />
    </div>
  </Tab>
</div>
